import { useState, useEffect } from 'react';
import ZoneEditModal from './ZoneEditModal';
import ZoneCreateModal from './ZoneCreateModal';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';

const ZoneManagement = () => {
    const { currentEvent } = useAppState();
    const [zones, setZones] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedZone, setSelectedZone] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch zones data when component mounts or currentEvent changes
    useEffect(() => {
        const fetchZones = async () => {
            if (!currentEvent || !currentEvent.id) return;
            
            setLoading(true);
            setError(null);
            
            try {
                const response = await api.get(`/events/${currentEvent.id}/zones`);
                setZones(response.data);
            } catch (error) {
                console.error('Error fetching zones:', error);
                setError('Failed to load zones. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        
        fetchZones();
    }, [currentEvent]);

    // Open edit modal
    const handleOpenEditModal = (zone) => {
        setSelectedZone(zone);
        setEditModalOpen(true);
    };

    // Create new zone
    const handleCreateZone = () => {
        setCreateModalOpen(true);
    };

    // Save zone changes
    const handleSaveZone = async (updatedZone) => {
        setSaveLoading(true);
        try {
            // Make the API call to update the zone
            await api.put(`/events/${currentEvent.id}/zones/${updatedZone.id}/`, updatedZone);
            
            // Update local state
            setZones(zones.map(zone =>
                zone.id === updatedZone.id ? updatedZone : zone
            ));
            
            setEditModalOpen(false);
            setSelectedZone(null);
        } catch (error) {
            console.error('Error updating zone:', error);
            alert('Failed to update zone. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    // Create a new zone
    const handleCreateNewZone = async (newZone) => {
        setSaveLoading(true);
        try {
            // Make the API call to create the zone
            const response = await api.post(`/events/${currentEvent.id}/zones/`, newZone);
            
            // Add the new zone to the list with the ID from the response
            setZones([...zones, response.data]);
            
            setCreateModalOpen(false);
        } catch (error) {
            console.error('Error creating zone:', error);
            alert('Failed to create zone. Please try again.');
        } finally {
            setSaveLoading(false);
        }
    };

    // Delete zone
    const handleDeleteZone = async (zoneId) => {
        if (window.confirm('Are you sure you want to delete this zone?')) {
            setLoading(true);
            try {
                await api.delete(`/events/${currentEvent.id}/zones/${zoneId}/`);
                setZones(zones.filter(zone => zone.id !== zoneId));
            } catch (error) {
                console.error('Error deleting zone:', error);
                alert('Failed to delete zone. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-center items-center py-12">
                    <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="ml-2 text-gray-700">Loading zones...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white shadow rounded-lg p-6">
                <div className="p-6 text-center text-red-500">{error}</div>
            </div>
        );
    }

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Zone Management</h2>
                <button
                    type="button"
                    onClick={handleCreateZone}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    Add New Zone
                </button>
            </div>

            {zones.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-8 text-center">
                    <p className="text-gray-500">No zones found for this event.</p>
                    <button
                        type="button"
                        onClick={handleCreateZone}
                        className="mt-4 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Create Your First Zone
                    </button>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Zone Name
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Zone Code
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ticket Types
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Single Check-in
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {zones.map((zone) => (
                                <tr key={zone.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium text-gray-900">{zone.name}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-gray-900">{zone.code}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">
                                            {zone.ticket_types && zone.ticket_types.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                    {zone.ticket_types.map((ticketType) => (
                                                        <span
                                                            key={ticketType.id}
                                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                        >
                                                            {ticketType.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-500 italic">All ticket types</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            zone.single_checkin 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {zone.single_checkin ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleOpenEditModal(zone)}
                                                className="text-indigo-600 hover:text-indigo-900"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteZone(zone.id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Zone Edit Modal */}
            {selectedZone && (
                <ZoneEditModal
                    zone={selectedZone}
                    isOpen={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setSelectedZone(null);
                    }}
                    onSave={handleSaveZone}
                    isLoading={saveLoading}
                />
            )}
            
            {/* Zone Create Modal */}
            <ZoneCreateModal
                isOpen={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSave={handleCreateNewZone}
                isLoading={saveLoading}
            />
        </div>
    );
};

export default ZoneManagement;